import {
	addObject,
	type EditKind,
	gravityAt,
	hitTest,
	moveTo,
	removeObject,
	resize,
	type Selection,
	updateAsteroids,
	updateFuel,
	updatePlanet,
} from "../game/map-edit";
import { type MapDef, type PlanetType, SHIP_THRUST } from "../sim";
import { type AsteroidSettings, CUSTOM_MAP_LIMITS, customMapProblems } from "../sim/maps";
import { PLANET_TYPES } from "../sim/maps/map-def";
import { el } from "./dom";
import type { PreviewImages } from "./map-preview";

export interface MapEditorOptions {
	map: MapDef;
	images: PreviewImages;
	/** Every change (the flow tracks "unsaved"). */
	onChange: (map: MapDef) => void;
	onSave: (map: MapDef) => void;
	onTestFly: (map: MapDef) => void;
	onExport: (map: MapDef) => void;
}

const PLANET_IMAGE: Record<PlanetType, keyof PreviewImages> = {
	mars: "planetMars",
	earth: "planetEarth",
	pluto: "planetPluto",
};
const PLANET_LABELS: Record<PlanetType, string> = { mars: "Mars", earth: "Earth", pluto: "Pluto" };
const KIND_LABELS: Record<EditKind, string> = {
	planet: "Planet",
	spawn: "Spawn point",
	fuel: "Fueling station",
};
const HISTORY = 50;
/** Gravity overlay grid (cells along the longer side). */
const FIELD_CELLS = 22;

/**
 * The map editor (P11.1): a canvas showing the whole world (drag objects to move them, the
 * gravity overlay shows the pull in units of ship thrust), a toolbar, a properties panel for
 * the selected object and the live list of problems. Pure DOM; all edits go through map-edit.
 */
export function mapEditor(options: MapEditorOptions): HTMLElement {
	let map = options.map;
	let selection: Selection | null = null;
	let showField = false;
	const history: MapDef[] = [];

	const canvas = el("canvas", { class: "editor-canvas", tabindex: 0 });
	canvas.setAttribute(
		"aria-label",
		"Map: drag planets, spawn points and fueling stations to move them",
	);
	const panel = el("div", { class: "editor-panel" });
	const problems = el("ul", { class: "editor-problems", "aria-live": "polite" });
	const nameInput = el("input", {
		type: "text",
		maxlength: CUSTOM_MAP_LIMITS.nameLength,
		value: map.name,
		id: "map-name",
	});
	const widthInput = numberInput(
		"map-width",
		map.size.w,
		CUSTOM_MAP_LIMITS.worldMin,
		CUSTOM_MAP_LIMITS.worldMax,
		100,
	);
	const heightInput = numberInput(
		"map-height",
		map.size.h,
		CUSTOM_MAP_LIMITS.worldMin,
		CUSTOM_MAP_LIMITS.worldMax,
		100,
	);
	// Asteroid field (P11.12): blank = the original game's value.
	const asteroidInputs = ASTEROID_FIELDS.map((f) => {
		const input = el("input", {
			type: "number",
			id: `map-asteroids-${f.key}`,
			min: f.min,
			max: f.max,
			step: f.step,
			placeholder: f.placeholder,
		});
		input.addEventListener("change", () => {
			const raw = input.value.trim();
			set(updateAsteroids(map, f.key, raw === "" ? undefined : Number(raw)));
		});
		return { ...f, input };
	});
	const save = button("Save", () => options.onSave(map));
	const testFly = button("Test fly", () => options.onTestFly(map));
	const fieldToggle = button("Gravity field", () => {
		showField = !showField;
		fieldToggle.setAttribute("aria-pressed", String(showField));
		draw();
	});
	fieldToggle.setAttribute("aria-pressed", "false");
	const undo = button("Undo", () => {
		const previous = history.pop();
		if (previous) set(previous, false);
	});
	const del = button("Delete", () => {
		if (selection) {
			const s = selection;
			selection = null;
			set(removeObject(map, s));
		}
	});

	/** Applies a new map version (recording the old one for undo) and redraws everything. */
	function set(next: MapDef, record = true): void {
		if (record) {
			history.push(map);
			if (history.length > HISTORY) history.shift();
		}
		map = next;
		if (selection && !itemAt(selection)) selection = null;
		options.onChange(map);
		refresh();
	}

	function itemAt(sel: Selection) {
		const list =
			sel.kind === "planet" ? map.planets : sel.kind === "spawn" ? map.spawnPoints : map.fuelItems;
		return list[sel.index];
	}

	function add(kind: EditKind): void {
		// New objects appear near the middle, a little apart from each other.
		const n = map.planets.length + map.spawnPoints.length + map.fuelItems.length;
		const angle = n * 2.4;
		const r = Math.min(map.size.w, map.size.h) * 0.3;
		const result = addObject(
			map,
			kind,
			map.size.w / 2 + Math.cos(angle) * r,
			map.size.h / 2 + Math.sin(angle) * r,
		);
		selection = result.selection;
		set(result.map);
	}

	// --- Canvas: world ↔ pixels ---
	function view() {
		const px = canvas.width;
		const scale = px / Math.max(map.size.w, map.size.h);
		return { px, scale, offX: (px - map.size.w * scale) / 2, offY: (px - map.size.h * scale) / 2 };
	}

	function toWorld(ev: PointerEvent): { x: number; y: number } {
		const rect = canvas.getBoundingClientRect();
		const v = view();
		const cx = ((ev.clientX - rect.left) / rect.width) * v.px;
		const cy = ((ev.clientY - rect.top) / rect.height) * v.px;
		return { x: (cx - v.offX) / v.scale, y: (cy - v.offY) / v.scale };
	}

	let dragging = false;
	canvas.addEventListener("pointerdown", (ev) => {
		const at = toWorld(ev);
		const slack = 8 / view().scale; // a few pixels of tolerance at any zoom
		selection = hitTest(map, at.x, at.y, slack);
		if (selection) {
			dragging = true;
			history.push(map);
			if (history.length > HISTORY) history.shift();
			canvas.setPointerCapture(ev.pointerId);
		}
		refresh();
	});
	canvas.addEventListener("pointermove", (ev) => {
		if (!dragging || !selection) return;
		const at = toWorld(ev);
		map = moveTo(map, selection, at.x, at.y);
		options.onChange(map);
		draw();
		renderPanel();
		renderProblems();
	});
	const endDrag = () => {
		dragging = false;
	};
	canvas.addEventListener("pointerup", endDrag);
	canvas.addEventListener("pointercancel", endDrag);
	canvas.addEventListener("keydown", (ev) => {
		if ((ev.key === "Delete" || ev.key === "Backspace") && selection) {
			ev.preventDefault();
			del.click();
		}
	});

	function draw(): void {
		const ctx = canvas.getContext("2d");
		if (!ctx) return;
		const { px, scale, offX, offY } = view();
		ctx.clearRect(0, 0, px, px);
		ctx.fillStyle = "#02030a";
		ctx.fillRect(0, 0, px, px);
		const bg = options.images.background;
		const w = map.size.w * scale;
		const h = map.size.h * scale;
		if (bg) ctx.drawImage(bg, offX, offY, w, h);
		else {
			ctx.fillStyle = "#05070f";
			ctx.fillRect(offX, offY, w, h);
		}
		ctx.strokeStyle = "rgb(120 150 230 / 45%)";
		ctx.lineWidth = 1;
		ctx.strokeRect(offX + 0.5, offY + 0.5, w - 1, h - 1);

		if (showField) drawField(ctx, scale, offX, offY);

		map.planets.forEach((p, i) => {
			const x = offX + p.x * scale;
			const y = offY + p.y * scale;
			const r = Math.max(2, p.radius * scale);
			const img = options.images[PLANET_IMAGE[p.type]];
			if (img) ctx.drawImage(img, x - r, y - r, r * 2, r * 2);
			else {
				ctx.fillStyle = "#7f8fb8";
				ctx.beginPath();
				ctx.arc(x, y, r, 0, Math.PI * 2);
				ctx.fill();
			}
			label(ctx, String(i + 1), x, y);
		});
		map.fuelItems.forEach((f) => {
			const x = offX + f.x * scale;
			const y = offY + f.y * scale;
			ctx.fillStyle = "#ffb347";
			ctx.beginPath();
			ctx.moveTo(x, y - 7);
			ctx.lineTo(x + 6, y);
			ctx.lineTo(x, y + 7);
			ctx.lineTo(x - 6, y);
			ctx.closePath();
			ctx.fill();
		});
		map.spawnPoints.forEach((s, i) => {
			const x = offX + s.x * scale;
			const y = offY + s.y * scale;
			ctx.strokeStyle = "#7fd0ff";
			ctx.lineWidth = 2;
			ctx.beginPath();
			ctx.arc(x, y, 7, 0, Math.PI * 2);
			ctx.stroke();
			label(ctx, String(i + 1), x, y - 14);
		});
		if (selection) {
			const item = itemAt(selection);
			if (item) {
				const r =
					selection.kind === "planet"
						? Math.max(6, (map.planets[selection.index]?.radius ?? 0) * scale) + 4
						: 12;
				ctx.strokeStyle = "#ffffff";
				ctx.setLineDash([5, 4]);
				ctx.lineWidth = 2;
				ctx.beginPath();
				ctx.arc(offX + item.x * scale, offY + item.y * scale, r, 0, Math.PI * 2);
				ctx.stroke();
				ctx.setLineDash([]);
			}
		}
	}

	/** Arrows in the direction of the pull; full length = the pull equals the ship's thrust. */
	function drawField(
		ctx: CanvasRenderingContext2D,
		scale: number,
		offX: number,
		offY: number,
	): void {
		const cell = Math.max(map.size.w, map.size.h) / FIELD_CELLS;
		const half = cell * scale * 0.45;
		ctx.lineWidth = 1.5;
		for (let wy = cell / 2; wy < map.size.h; wy += cell) {
			for (let wx = cell / 2; wx < map.size.w; wx += cell) {
				const a = gravityAt(map, wx, wy);
				const mag = Math.hypot(a.x, a.y);
				if (mag < 1e-5) continue;
				const share = mag / SHIP_THRUST;
				const len = half * Math.min(1, Math.sqrt(share));
				const x = offX + wx * scale;
				const y = offY + wy * scale;
				const ex = x + (a.x / mag) * len;
				const ey = y + (a.y / mag) * len;
				ctx.strokeStyle = share >= 1 ? "rgb(255 90 90 / 85%)" : "rgb(160 220 255 / 55%)";
				ctx.beginPath();
				ctx.moveTo(x, y);
				ctx.lineTo(ex, ey);
				ctx.stroke();
				ctx.fillStyle = ctx.strokeStyle;
				ctx.beginPath();
				ctx.arc(ex, ey, 1.6, 0, Math.PI * 2);
				ctx.fill();
			}
		}
	}

	function renderPanel(): void {
		panel.replaceChildren();
		if (!selection) {
			panel.append(
				el("p", { class: "page-text" }, "Select an object on the map to edit it, or add one."),
			);
			return;
		}
		const sel = selection;
		const item = itemAt(sel);
		if (!item) return;
		panel.append(el("h2", {}, `${KIND_LABELS[sel.kind]} ${sel.index + 1}`));
		panel.append(
			field(
				"X",
				numberInput("obj-x", item.x, 0, map.size.w - 1, 10, (v) =>
					set(moveTo(map, sel, v, item.y)),
				),
			),
			field(
				"Y",
				numberInput("obj-y", item.y, 0, map.size.h - 1, 10, (v) =>
					set(moveTo(map, sel, item.x, v)),
				),
			),
		);
		if (sel.kind === "planet") {
			const p = map.planets[sel.index];
			if (!p) return;
			const type = el(
				"select",
				{ id: "obj-type" },
				...PLANET_TYPES.map((t) =>
					el("option", { value: t, selected: t === p.type }, PLANET_LABELS[t]),
				),
			);
			type.addEventListener("change", () =>
				set(updatePlanet(map, sel.index, { type: type.value as PlanetType })),
			);
			const L = CUSTOM_MAP_LIMITS;
			panel.append(
				field("Type", type),
				field(
					"Radius",
					numberInput("obj-radius", p.radius, L.radiusMin, L.radiusMax, 10, (v) =>
						set(updatePlanet(map, sel.index, { radius: v })),
					),
				),
				field(
					"Mass",
					numberInput("obj-mass", p.mass, L.massMin, L.massMax, 10, (v) =>
						set(updatePlanet(map, sel.index, { mass: v })),
					),
				),
			);
		}
		if (sel.kind === "fuel") {
			const f = map.fuelItems[sel.index];
			if (f) {
				const L = CUSTOM_MAP_LIMITS;
				panel.append(
					field(
						"Refuel rate",
						numberInput("obj-fuel", f.amount, L.fuelRateMin, L.fuelRateMax, 1, (v) =>
							set(updateFuel(map, sel.index, v)),
						),
					),
				);
			}
		}
		panel.append(del);
	}

	function renderProblems(): void {
		const list = customMapProblems(map);
		problems.replaceChildren(...list.map((p) => el("li", {}, p)));
		problems.hidden = list.length === 0;
		save.disabled = list.length > 0;
		testFly.disabled = list.length > 0;
	}

	function refresh(): void {
		nameInput.value = map.name;
		widthInput.value = String(map.size.w);
		heightInput.value = String(map.size.h);
		for (const f of asteroidInputs) {
			const v = map.asteroids?.[f.key];
			f.input.value = v === undefined ? "" : String(v);
		}
		undo.disabled = history.length === 0;
		draw();
		renderPanel();
		renderProblems();
	}

	nameInput.addEventListener("input", () => {
		map = { ...map, name: nameInput.value };
		options.onChange(map);
		renderProblems();
	});
	const onSize = () =>
		set(
			resize(map, Number(widthInput.value) || map.size.w, Number(heightInput.value) || map.size.h),
		);
	widthInput.addEventListener("change", onSize);
	heightInput.addEventListener("change", onSize);

	const root = el(
		"div",
		{ class: "map-editor" },
		el(
			"div",
			{ class: "editor-toolbar" },
			field("Name", nameInput),
			field("Width", widthInput),
			field("Height", heightInput),
		),
		el(
			"div",
			{ class: "editor-toolbar editor-asteroids" },
			...asteroidInputs.map((f) => field(f.label, f.input)),
		),
		el(
			"div",
			{ class: "editor-tools" },
			button("+ Planet", () => add("planet")),
			button("+ Spawn point", () => add("spawn")),
			button("+ Fueling station", () => add("fuel")),
			fieldToggle,
			undo,
		),
		el("div", { class: "editor-main" }, canvas, panel),
		problems,
		el(
			"div",
			{ class: "editor-actions" },
			save,
			testFly,
			button("Save file", () => options.onExport(map)),
		),
	);

	// Canvas resolution follows its CSS size (sharp on high-DPI screens).
	const fit = () => {
		const css = canvas.getBoundingClientRect().width || 560;
		const px = Math.round(css * Math.min(window.devicePixelRatio || 1, 2));
		if (canvas.width !== px) {
			canvas.width = px;
			canvas.height = px;
			draw();
		}
	};
	new ResizeObserver(fit).observe(canvas);
	canvas.width = 560;
	canvas.height = 560;
	refresh();
	return root;
}

const ASTEROID_FIELDS: Array<{
	key: keyof AsteroidSettings;
	label: string;
	min: number;
	max: number;
	step: number;
	placeholder: string;
}> = [
	{
		key: "perMinute",
		label: "Asteroids per minute",
		min: 0,
		max: CUSTOM_MAP_LIMITS.asteroidsPerMinuteMax,
		step: 1,
		placeholder: "20",
	},
	{
		key: "max",
		label: "Most at once",
		min: 0,
		max: CUSTOM_MAP_LIMITS.asteroidsMax,
		step: 1,
		placeholder: "No limit",
	},
	{
		key: "speed",
		label: "Average speed",
		min: CUSTOM_MAP_LIMITS.asteroidSpeedMin,
		max: CUSTOM_MAP_LIMITS.asteroidSpeedMax,
		step: 0.5,
		placeholder: "3",
	},
];

function label(ctx: CanvasRenderingContext2D, text: string, x: number, y: number): void {
	ctx.font = "bold 11px system-ui, sans-serif";
	ctx.textAlign = "center";
	ctx.textBaseline = "middle";
	ctx.lineWidth = 3;
	ctx.strokeStyle = "rgb(0 0 0 / 70%)";
	ctx.strokeText(text, x, y);
	ctx.fillStyle = "#ffffff";
	ctx.fillText(text, x, y);
}

function button(text: string, onClick: () => void): HTMLButtonElement {
	return el("button", { class: "btn btn-small", type: "button", onclick: onClick }, text);
}

function field(text: string, control: HTMLElement): HTMLElement {
	if (control.id)
		return el("label", { class: "editor-field", for: control.id }, el("span", {}, text), control);
	return el("label", { class: "editor-field" }, el("span", {}, text), control);
}

function numberInput(
	id: string,
	value: number,
	min: number,
	max: number,
	step: number,
	onChange?: (v: number) => void,
): HTMLInputElement {
	const input = el("input", {
		type: "number",
		id,
		value: String(Math.round(value)),
		min,
		max,
		step,
	});
	if (onChange) {
		input.addEventListener("change", () => {
			const v = Number(input.value);
			if (Number.isFinite(v)) onChange(v);
		});
	}
	return input;
}
